import React, { useEffect } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { AppInput } from 'components/common';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { resetPasswordSchema } from './reset-password.schema';
import { useTranslation } from 'react-i18next';
import { ALERT_TYPE, TKeys } from 'utils/enums';
import {
    changeI18nLocale,
    getLocalStorageValue,
    getMessageText,
    removeLocalStorageValue,
    setLocalStorageValue,
} from 'utils/helpers';
import { resetPasswordFetcher } from './reset-password.fetcher';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { hideLoading, showAlert } from 'reducer/rootReducer';
import { setUser } from 'reducer/userReducer';
import { APP_NAME } from 'utils/constants';
import { loginFetcher } from '../login/login.fetcher';

const ResetPasswordPage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const user = useSelector((state: RootState) => state.user.user);

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(resetPasswordSchema(t)),
        defaultValues: { password: '', confirmPassword: '' },
    });

    const handleResetPassword = async (password: string) => {
        const res = await resetPasswordFetcher(user?.user_id ?? '', { password });

        if (!res.success) {
            dispatch(
                showAlert({
                    status: true,
                    message: res.error,
                    type: ALERT_TYPE.ERROR,
                }),
            );
            return;
        }

        const contract_code = getLocalStorageValue('contract') ?? '';

        const body: IRequestLogin = {
            username: user?.user_id ?? '',
            password,
            contract_code: contract_code ?? '',
        };

        const resLogin = await loginFetcher(body);
        if (resLogin instanceof Error) {
            const newAlert: IAppAlert = {
                status: true,
                type: ALERT_TYPE.ERROR,
                message: resLogin.message,
            };
            dispatch(hideLoading());
            dispatch(showAlert(newAlert));

            console.error(`Get with error: ${resLogin.message}`);
            return;
        }

        await changeI18nLocale(resLogin.data.user.language);

        // Success
        dispatch(setUser(resLogin.data.user));

        setLocalStorageValue(
            'authParams',
            JSON.stringify({
                accessToken: resLogin.data.access_token,
                refreshToken: resLogin.data.refresh_token,
                loginTime: `${Date.now()}`,
                user: resLogin.data.user,
            }),
        );

        dispatch(
            showAlert({
                status: true,
                message: getMessageText(t(TKeys.MSI00013)),
                type: ALERT_TYPE.SUCCESS,
            }),
        );
    };

    useEffect(() => {
        const handleBeforeUnload = () => {
            dispatch(setUser(undefined));
            removeLocalStorageValue('authParams');
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="100vh"
            bgcolor="#f8f9fa">
            <Box
                component="img"
                src={require('assets/images/banner-logo.png')}
                alt="Logo"
                width={120}
                mb={2}
            />

            <Typography variant="h5" fontWeight="bold" color="#337ab7" mb={3}>
                {APP_NAME}
            </Typography>

            <Paper elevation={3} sx={{ p: 4, width: 500, textAlign: 'center' }}>
                <form
                    noValidate
                    autoComplete="off"
                    onSubmit={handleSubmit((data) => {
                        handleResetPassword(data.password);
                    })}>
                    <Stack direction="column" spacing={4}>
                        <AppInput
                            name="password"
                            register={register}
                            setValue={setValue}
                            label={t(TKeys.LBL010014)}
                            required
                            placeHolder={getMessageText(t(TKeys.MSE00001), t(TKeys.LBL010014))}
                            password
                            error={!!errors.password}
                            helperText={errors.password?.message}
                        />

                        <AppInput
                            name="confirmPassword"
                            register={register}
                            setValue={setValue}
                            label={t(TKeys.LBL010016)}
                            required
                            placeHolder={getMessageText(t(TKeys.MSE00001), t(TKeys.LBL010016))}
                            password
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword?.message}
                        />

                        <Button
                            type="submit"
                            aria-label="Submit"
                            sx={{ fontSize: '16px', height: '50px' }}
                            variant="contained"
                            color="primary"
                            fullWidth>
                            {t(TKeys.BTN010002)}
                        </Button>

                        <Typography
                            onClick={() => {
                                dispatch(setUser(undefined));
                                removeLocalStorageValue('authParams');
                            }}
                            sx={{ fontSize: '16px', cursor: 'pointer' }}
                            variant="body2"
                            color="textSecondary">
                            {t(TKeys.BTN010003)}
                        </Typography>
                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default ResetPasswordPage;
